/**
 * @module app.cm
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { apiCall } from 'app/shared/lib/essette-api';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { Toaster } from 'app/shared/lib/toaster';
import { deepClone } from 'app/shared/lib/utilities';
import { getReference } from 'app/shared/models/essette-types';
import { validate } from 'app/shared/elements/ess-rule';
import { CaseService } from 'app/cm/services/case-service';
import { HouseholdCaseStatusHistoryGrid } from 'app/cm/components/household/household-case-status-history-grid';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import * as CM from 'app/cm/models/essette-api';

@autoinject()
export class EditHouseholdCaseStatus extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull({ changeHandler: 'reload' })
    protected status: CM.HouseholdCaseStatus | null = null;

    protected editedStatus: CM.HouseholdCaseStatus | null = null;

    @bindable.objectNull({ defaultBindingMode: bindingMode.twoWay })
    protected householdCase: CM.HouseholdCase | null = null;

    protected statuses: ReadonlyArray<CM.HouseholdCaseStatus> = [];

    @bindable.boolean
    protected readonly: boolean = true;

    protected validate = validate;


    ////****************** Element References ***************************************////
    protected $statusHistory!: HouseholdCaseStatusHistoryGrid


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CaseService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.reload();
    }


    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        this.loadHouseholdCaseStatuses();
    }


    ////****************** Functions ************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public reset(): void {
        this.editedStatus = null;
    }

    public async submit(): Promise<boolean> {
        return await this.updateHouseholdCaseStatus();
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadHouseholdCaseStatuses(): Promise<void> {
        try {
            this.state.beginLoad();

            const options: Partial<IQueryLoadOptions> = {
                all: true
            };

            const response = await this.service.loadHouseholdCaseStatuses(options);

            this.statuses = response.results;
            if(this.householdCase?.status?.id) {
                this.statuses = this.statuses.filter(status => status.id !== this.householdCase?.status?.id);
            }

        }
        catch(error) {
            this.logger.error('Error Loading Household Case Statuses', error);
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async updateHouseholdCaseStatus(): Promise<boolean> {
        if(!this.editedStatus || !this.householdCase) return false;

        const saveHouseholdCase: CM.HouseholdCase = {
            id: this.householdCase.id,
            status: getReference(this.editedStatus)
        }

        let isSuccessful: boolean = false;
        try {
            this.state.beginSave();
            const response = await this.service.updateHouseholdCaseStatus(saveHouseholdCase);
            isSuccessful = response.isSuccessful;
            if(isSuccessful) {
                this.householdCase.status = deepClone(this.editedStatus);
                this.editedStatus = null;
                this.$statusHistory.reload();
                this.loadHouseholdCaseStatuses();
            }
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.saved('cm:householdCaseStatus.$', isSuccessful);
            this.state.endSave();
            return isSuccessful;
        }
    }
}
